import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useSeo } from "@/hooks/useSeo";
import { Scale } from "lucide-react";

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl sm:text-3xl font-semibold text-emerald-900 mt-12 mb-4 scroll-mt-24">
    {children}
  </h2>
);
const P = ({ children }) => <p className="text-stone-700 leading-relaxed mb-4">{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-stone-700 leading-relaxed mb-4">
    {items.map((it, i) => (<li key={i}>{it}</li>))}
  </ul>
);
const HR = () => <hr className="border-stone-100 my-2" />;

export default function AGB() {
  useSeo(
    "Allgemeine Geschäftsbedingungen (AGB) – LAIzyones",
    "Allgemeine Geschäftsbedingungen von LAIzyones für die Nutzung der Softwarelösung LAIzyDocu (B2B – Praxen, Heilpraktiker u. a.).",
  );

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="agb-page">
      <SiteNav />

      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-6">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-sm font-medium">
          <Scale className="w-4 h-4" /> Rechtliches
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-emerald-900 leading-[1.05] mt-6">
          Allgemeine Geschäftsbedingungen
        </h1>
        <p className="text-stone-600 mt-4">für die Nutzung der Softwarelösung von LAIzyones · Stand: Juni 2026</p>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-20">
        <div className="bg-white rounded-[2rem] border border-stone-100 shadow-[0_20px_60px_rgb(0,0,0,0.05)] p-7 md:p-12 text-base">

          <H2>§ 1 Anbieter</H2>
          <P>Anbieter der Softwarelösung ist:</P>
          <div className="rounded-2xl bg-stone-50 border border-stone-100 p-6 mb-4 leading-relaxed text-stone-700">
            <p className="font-semibold text-emerald-900">LAIzyones</p>
            <p>Einzelunternehmen</p>
            <p className="mt-3"><span className="font-semibold">Inhaber:</span> David Ehrle</p>
            <p className="mt-3">Ferdinand-Lentjes-Straße 32<br />39167 Wellen<br />Deutschland</p>
            <p className="mt-3">
              Telefon: 0179 7615911<br />
              E-Mail: <a href="mailto:info@laizyones.de" className="text-emerald-700 underline">info@laizyones.de</a><br />
              Website: <a href="https://laizyones.de" target="_blank" rel="noreferrer" className="text-emerald-700 underline">https://laizyones.de</a>
            </p>
          </div>
          <P>Die angebotene Software ist ein Produkt und eine Marke von LAIzyones.</P>
          <HR />

          <H2>§ 2 Geltungsbereich</H2>
          <P>
            Diese Allgemeinen Geschäftsbedingungen gelten für sämtliche Verträge zwischen LAIzyones und gewerblichen
            Kunden, insbesondere Physiotherapiepraxen, Heilpraktikern, Chiropraktikern und vergleichbaren Einrichtungen.
          </P>
          <P>Die Nutzung durch Verbraucher im Sinne des § 13 BGB ist ausgeschlossen.</P>
          <P>Mit Registrierung oder Nutzung der Software erkennt der Kunde diese AGB an.</P>
          <HR />

          <H2>§ 3 Vertragsgegenstand</H2>
          <P>
            LAIzyones stellt eine cloudbasierte Softwarelösung zur digitalen Erfassung, Verwaltung und Archivierung
            von Dokumenten bereit.
          </P>
          <P>Die Software ermöglicht insbesondere:</P>
          <UL items={[
            "digitale Datenschutzerklärungen",
            "digitale Behandlungsverträge",
            "elektronische Signaturen",
            "PDF-Erstellung",
            "Dokumentenverwaltung",
            "Dokumentenarchivierung",
            "Benutzer- und Rollenverwaltung",
            "Zugriff über QR-Codes",
            "digitale Erfassung von Dokumenten auf Tablets und anderen Endgeräten",
          ]} />
          <P>Der konkrete Funktionsumfang ergibt sich aus der jeweils aktuellen Produktbeschreibung.</P>
          <P>
            LAIzyones behält sich vor, Funktionen weiterzuentwickeln, anzupassen oder zu erweitern, soweit hierdurch
            der vertragsgemäße Zweck nicht beeinträchtigt wird.
          </P>
          <HR />

          <H2>§ 4 Testzugang</H2>
          <P>LAIzyones kann Interessenten einen kostenfreien Testzugang zur Verfügung stellen.</P>
          <P>Der Testzugang dient ausschließlich der Evaluierung der Software.</P>
          <P>Die Testversion kann Fehler, Funktionsänderungen, Einschränkungen oder vorübergehende Ausfälle enthalten.</P>
          <P>Für die Testversion wird keine bestimmte Verfügbarkeit, Fehlerfreiheit oder Vollständigkeit zugesichert.</P>
          <P>Der Kunde bleibt während der Testphase vollständig für die Sicherung seiner Daten verantwortlich.</P>
          <P>
            Es wird ausdrücklich empfohlen, sämtliche während der Testphase erzeugten oder gespeicherten Dokumente
            zusätzlich in eigenen Systemen zu archivieren.
          </P>
          <P>Die Testversion ist nicht als alleinige Archivierungs-, Dokumentations- oder Aufbewahrungslösung vorgesehen.</P>
          <P>LAIzyones kann Testzugänge jederzeit ohne Angabe von Gründen beenden oder einschränken.</P>
          <HR />

          <H2>§ 5 Registrierung und Benutzerkonten</H2>
          <P>Die Nutzung der Software setzt die Erstellung eines Benutzerkontos voraus.</P>
          <P>Der Kunde verpflichtet sich, sämtliche Angaben vollständig und wahrheitsgemäß zu machen.</P>
          <P>Zugangsdaten sind vertraulich zu behandeln und vor dem Zugriff Dritter zu schützen.</P>
          <P>
            Der Kunde ist verpflichtet, LAIzyones unverzüglich über jeden Verdacht einer missbräuchlichen Nutzung
            oder eines unbefugten Zugriffs zu informieren.
          </P>
          <HR />

          <H2>§ 6 Zwei-Faktor-Authentifizierung</H2>
          <P>Zur Erhöhung der Systemsicherheit verwendet die Plattform eine Zwei-Faktor-Authentifizierung (2FA).</P>
          <P>Der Kunde ist verpflichtet, die bereitgestellten Sicherheitsmechanismen ordnungsgemäß zu nutzen.</P>
          <P>Ein Verlust von Authentifizierungsdaten ist unverzüglich mitzuteilen.</P>
          <HR />

          <H2>§ 7 Laufzeit und Kündigung</H2>
          <P>Nach Ablauf eines Testzeitraums kann die Software kostenpflichtig genutzt werden.</P>
          <P>Der Vertrag wird auf unbestimmte Zeit geschlossen.</P>
          <P>Der Kunde kann den Vertrag jederzeit mit Wirkung zum Ende des laufenden Abrechnungsmonats kündigen.</P>
          <P>LAIzyones kann den Vertrag mit einer Frist von 30 Tagen zum Ende eines Kalendermonats kündigen.</P>
          <P>Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt.</P>
          <HR />

          <H2>§ 8 Datenexport nach Vertragsende</H2>
          <P>
            Nach Wirksamwerden einer Kündigung bleibt der Zugang zu den gespeicherten Daten für einen Zeitraum von
            30 Kalendertagen im Lesemodus verfügbar.
          </P>
          <P>Während dieses Zeitraums kann der Kunde sämtliche ihm zugeordneten Daten und Dokumente exportieren und sichern.</P>
          <P>
            Nach Ablauf der 30-tägigen Exportfrist ist LAIzyones berechtigt, sämtliche kundenspezifischen Daten
            dauerhaft zu löschen, soweit keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
          </P>
          <P>Der Kunde ist selbst dafür verantwortlich, seine Daten rechtzeitig zu exportieren und zu archivieren.</P>
          <P>Ein Anspruch auf Wiederherstellung von Daten nach deren endgültiger Löschung besteht nicht.</P>
          <HR />

          <H2>§ 9 Preise und Zahlungsbedingungen</H2>
          <P>
            Die jeweils gültigen Preise ergeben sich aus den zum Zeitpunkt des Vertragsabschlusses veröffentlichten
            Preisangaben oder individuellen Angeboten.
          </P>
          <P>Rechnungen sind innerhalb der jeweils angegebenen Zahlungsfrist ohne Abzug fällig.</P>
          <P>
            Bei Zahlungsverzug ist LAIzyones berechtigt, den Zugang zur Software vorübergehend einzuschränken oder
            zu sperren.
          </P>
          <HR />

          <H2>§ 10 Verantwortlichkeiten des Kunden</H2>
          <P>
            Der Kunde bleibt allein verantwortlich für sämtliche Inhalte, Daten und Dokumente, die über die Plattform
            verarbeitet werden.
          </P>
          <P>Insbesondere ist der Kunde verantwortlich für:</P>
          <UL items={[
            "die Rechtmäßigkeit der Datenverarbeitung",
            "die Einhaltung datenschutzrechtlicher Vorschriften",
            "die Einholung erforderlicher Einwilligungen",
            "die Erfüllung gesetzlicher Informationspflichten",
            "die Einhaltung berufsrechtlicher Vorgaben",
            "die Einhaltung gesetzlicher Aufbewahrungsfristen",
            "die Richtigkeit der gespeicherten Daten",
          ]} />
          <P>Der Kunde bleibt Verantwortlicher im Sinne der DSGVO.</P>
          <P>
            Die Nutzung der Software entbindet den Kunden nicht von gesetzlichen Dokumentations-, Archivierungs- oder
            Nachweispflichten.
          </P>
          <HR />

          <H2>§ 11 Datenschutz und Auftragsverarbeitung</H2>
          <P>
            Soweit personenbezogene Daten verarbeitet werden, erfolgt die Verarbeitung durch LAIzyones ausschließlich
            als Auftragsverarbeiter gemäß Art. 28 DSGVO.
          </P>
          <P>Zwischen LAIzyones und dem Kunden wird ein gesonderter Auftragsverarbeitungsvertrag (AVV) geschlossen.</P>
          <P>Die Verarbeitung erfolgt ausschließlich nach Weisung des Kunden und im Rahmen der gesetzlichen Vorgaben.</P>
          <HR />

          <H2>§ 12 Elektronische Signaturen und Dokumente</H2>
          <P>Die Software ermöglicht die elektronische Erstellung und Unterzeichnung von Dokumenten.</P>
          <P>Zur Nachweisbarkeit können insbesondere gespeichert werden:</P>
          <UL items={[
            "elektronische Signaturen",
            "Zeitstempel",
            "Dokumentenversionen",
            "Dokumenten-IDs",
            "technische Nachweisdaten",
          ]} />
          <P>Der Kunde erkennt an, dass diese Daten der Dokumentation und Nachweisführung dienen.</P>
          <P>
            LAIzyones übernimmt keine Gewähr dafür, dass elektronische Signaturen in jedem Einzelfall bestimmte
            gesetzliche Formvorschriften erfüllen.
          </P>
          <P>Die Prüfung der rechtlichen Verwendbarkeit eines Dokuments obliegt ausschließlich dem Kunden.</P>
          <P>LAIzyones erbringt keine Rechtsberatung.</P>
          <HR />

          <H2>§ 13 Verfügbarkeit der Plattform</H2>
          <P>LAIzyones bemüht sich um eine möglichst hohe Verfügbarkeit der Plattform.</P>
          <P>Eine ununterbrochene oder jederzeit fehlerfreie Verfügbarkeit wird nicht geschuldet.</P>
          <P>Einschränkungen können insbesondere entstehen durch:</P>
          <UL items={[
            "Wartungsarbeiten",
            "Sicherheitsupdates",
            "technische Störungen",
            "höhere Gewalt",
            "Ausfälle von Hosting- oder Cloud-Anbietern",
            "Ausfälle von Telekommunikationsnetzen",
          ]} />
          <P>Geplante Wartungsarbeiten werden nach Möglichkeit rechtzeitig angekündigt.</P>
          <HR />

          <H2>§ 14 Datensicherung</H2>
          <P>LAIzyones führt technische Sicherungsmaßnahmen und Backups durch.</P>
          <P>
            Unabhängig davon bleibt der Kunde verpflichtet, wichtige Daten und Dokumente regelmäßig selbst zu
            exportieren und zu archivieren.
          </P>
          <P>
            LAIzyones übernimmt keine Haftung für Schäden, die dadurch entstehen, dass der Kunde keine eigenen
            Datensicherungen vorgenommen hat.
          </P>
          <HR />

          <H2>§ 15 Nutzungsbeschränkungen</H2>
          <P>Dem Kunden ist insbesondere untersagt:</P>
          <UL items={[
            "Sicherheitsmechanismen zu umgehen",
            "auf Daten anderer Kunden zuzugreifen",
            "Schadsoftware einzubringen",
            "automatisierte Angriffe durchzuführen",
            "die Plattform rechtswidrig zu nutzen",
            "technische Schutzmaßnahmen zu umgehen",
          ]} />
          <HR />

          <H2>§ 16 Sperrung von Zugängen</H2>
          <P>LAIzyones ist berechtigt, Benutzerkonten vorübergehend oder dauerhaft zu sperren, wenn:</P>
          <UL items={[
            "ein Verstoß gegen diese AGB vorliegt",
            "Sicherheitsrisiken bestehen",
            "gesetzliche Verpflichtungen dies erfordern",
            "ein Missbrauchsverdacht besteht",
            "Zahlungsrückstände vorliegen",
          ]} />
          <HR />

          <H2>§ 17 Gewährleistung</H2>
          <P>Für kostenpflichtige Leistungen gelten die gesetzlichen Gewährleistungsrechte.</P>
          <P>
            Bei unentgeltlichen Testzugängen erfolgt die Bereitstellung unter Ausschluss jeglicher Gewährleistung,
            soweit gesetzlich zulässig.
          </P>
          <HR />

          <H2>§ 18 Haftung</H2>
          <P>LAIzyones haftet uneingeschränkt:</P>
          <UL items={[
            "bei Vorsatz",
            "bei grober Fahrlässigkeit",
            "bei Verletzung von Leben, Körper oder Gesundheit",
            "in Fällen zwingender gesetzlicher Haftung",
          ]} />
          <P>Bei einfacher Fahrlässigkeit haftet LAIzyones nur bei Verletzung wesentlicher Vertragspflichten.</P>
          <P>In diesen Fällen ist die Haftung auf den vertragstypischen, vorhersehbaren Schaden begrenzt.</P>
          <P>
            Eine Haftung für mittelbare Schäden, Folgeschäden, entgangenen Gewinn, Betriebsunterbrechungen oder
            Datenverluste ist ausgeschlossen, soweit gesetzlich zulässig.
          </P>
          <P>
            Die Haftung für Datenverluste ist auf den Aufwand beschränkt, der bei ordnungsgemäßer Datensicherung
            durch den Kunden erforderlich gewesen wäre.
          </P>
          <HR />

          <H2>§ 19 Höhere Gewalt</H2>
          <P>Keine Partei haftet für Verzögerungen oder Leistungsausfälle aufgrund von Ereignissen außerhalb ihres Einflussbereichs.</P>
          <P>Hierzu zählen insbesondere:</P>
          <UL items={[
            "Naturkatastrophen",
            "Krieg",
            "Terrorismus",
            "behördliche Maßnahmen",
            "Stromausfälle",
            "Ausfälle von Cloud- oder Hostinganbietern",
            "Cyberangriffe auf Dritte",
            "Ausfälle öffentlicher Telekommunikationsnetze",
          ]} />
          <HR />

          <H2>§ 20 Geistiges Eigentum</H2>
          <P>
            Sämtliche Rechte an der Software, den Quellcodes, Designs, Funktionen, Dokumentationen und Marken
            verbleiben bei LAIzyones.
          </P>
          <P>
            Der Kunde erhält für die Dauer des Vertrages ein einfaches, nicht ausschließliches und nicht
            übertragbares Nutzungsrecht.
          </P>
          <P>
            Eine Weitergabe, Vermietung, Unterlizenzierung oder Vervielfältigung außerhalb der bestimmungsgemäßen
            Nutzung ist nicht gestattet.
          </P>
          <HR />

          <H2>§ 21 Änderungen der AGB</H2>
          <P>LAIzyones ist berechtigt, diese AGB anzupassen, sofern hierfür ein sachlicher Grund besteht.</P>
          <P>Hierzu zählen insbesondere:</P>
          <UL items={[
            "Änderungen gesetzlicher Vorgaben",
            "technische Weiterentwicklungen",
            "Sicherheitsanforderungen",
            "Änderungen des Leistungsumfangs",
          ]} />
          <P>Änderungen werden dem Kunden rechtzeitig mitgeteilt.</P>
          <HR />

          <H2>§ 22 Schlussbestimmungen</H2>
          <P>Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts.</P>
          <P>
            Gerichtsstand für sämtliche Streitigkeiten aus oder im Zusammenhang mit diesem Vertrag ist, soweit
            gesetzlich zulässig, der Sitz von LAIzyones.
          </P>
          <P>
            Sollten einzelne Bestimmungen dieser AGB ganz oder teilweise unwirksam sein oder werden, bleibt die
            Wirksamkeit der übrigen Bestimmungen unberührt.
          </P>
          <P>
            An die Stelle der unwirksamen Bestimmung tritt die gesetzlich zulässige Regelung, die dem
            wirtschaftlichen Zweck der ursprünglichen Bestimmung am nächsten kommt.
          </P>
          <p className="text-sm text-stone-500 mt-8">Stand: Juni 2026</p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
